import React from 'react';
import { useTranslation } from 'react-i18next';
import { useMediaQuery } from 'react-responsive';
import classNames from 'classnames';
import { Form, Formik } from 'formik';

import Button from 'components/Common/Button/Button';
import DateFieldCustom from 'components/Common/Formik/DateFieldCustom/DateFieldCustom';
import { ExtraStyleType } from 'components/Common/Formik/FieldCustom/FieldCustom';
import RadioFieldCustom from 'components/Common/Formik/RadioFieldCustom/RadioFieldCustom';
import ScrollToFieldError from 'components/Common/Formik/ScrollToFieldError/ScrollToFieldError';
import FormikErrorsGtmLogger from 'components/Common/FormikErrorsGtmLogger/FormikErrorsGtmLogger';
import TextFieldCustom from 'components/Common/Formik/TextFieldCustom/TextFieldCustom';
import RequiredFields from 'components/Common/RequiredFields/RequiredFields';
import useCurrentCountry from 'hooks/useCurrentCountry';
import { useCustomer } from 'hooks/useCustomer';
import useFlashMessage from 'hooks/useFlashMessage';
import { genderOptions, genderOptionsUk } from 'model/CommonFields';
import validationSchema from 'schemas/AccountPersonalInformation';
import { apiUrls } from 'services/api-urls';
import { fetcherWithBody } from 'services/fetcher';
import {
  replacePhoneNumberWithoutSpace,
  replacePhoneNumberWithSpace,
} from 'services/formatter';
import { AC } from 'settings/form';
import { SMALL_DESKTOP } from 'settings/mediaQuery';
import { FIELDS_REGEX } from 'settings/regex';

import styles from './PersonalDetailsInformations.module.scss';

function PersonalDetailsInformations(): JSX.Element {
  const { setFlashMessage } = useFlashMessage();
  const { t } = useTranslation(['personalDetailsPage']);
  const { customer, refreshCustomer, smsOptin } = useCustomer();
  const { isUk } = useCurrentCountry();
  const isMobileAndPortraitTablet = useMediaQuery({
    query: SMALL_DESKTOP,
  });

  const isDisabledDate = customer?.dateOfBirth !== '' && isUk;

  const extraStyleField: ExtraStyleType = {
    container: styles.container,
    fieldContainer: styles.fieldContainer,
    label: styles.fieldLabel,
  };

  const extraStyleBirth: ExtraStyleType = {
    container: classNames(styles.container, styles.containerBirth),
    label: classNames(styles.fieldLabel, styles.fieldLabelBirth),
    fieldContainer: classNames(
      styles.fieldContainer,
      styles.fieldContainerBirth
    ),
  };

  const extraStyleGender: ExtraStyleType = {
    container: classNames(styles.container, styles.containerGender),
    fieldContainer: classNames(
      styles.fieldContainer,
      styles.fieldContainerGender
    ),
    label: classNames(styles.fieldLabel, styles.fieldLabelGender),
  };

  const extraStyleRadio: ExtraStyleType = {
    container: classNames(styles.container, styles.containerRadio),
    fieldContainer: classNames(
      styles.fieldContainer,
      styles.fieldContainerRadio
    ),
    label: classNames(styles.fieldLabel, styles.fieldLabelRadio),
    labelErrorMessage: styles.smsmRadioError,
  };

  const params = {
    civility: customer?.civilityNumber,
    civilityNumber: customer?.civilityNumber,
    lastName: customer?.lastName,
    firstName: customer?.firstName,
    dateOfBirth: customer?.dateOfBirth,
    phone: customer?.phone && replacePhoneNumberWithSpace(customer?.phone),
    cellPhone:
      customer?.cellPhone && replacePhoneNumberWithSpace(customer?.cellPhone),
    smsOption: smsOptin ?? '',
  };

  const smsOptions = [
    {
      label: t(`LABEL_YES`, { ns: 'translation' }),
      value: 'O',
    },
    {
      label: t(`LABEL_NO`, { ns: 'translation' }),
      value: 'N',
    },
  ];

  const onSubmit = async (values: typeof params) => {
    const { cellPhone, phone, smsOption, ...rest } = values;
    const cleanedCellPhone = replacePhoneNumberWithoutSpace(cellPhone || '');
    const body = {
      ...rest,
      cellPhone: cleanedCellPhone,
      phone: replacePhoneNumberWithoutSpace(phone || ''),
      smsOption: cleanedCellPhone ? smsOption : null,
    };

    if (isDisabledDate) {
      delete body.dateOfBirth;
    }

    const response = await fetcherWithBody(apiUrls.getCustomerUpdate(), body);
    if (response.message === 'OK') {
      setFlashMessage(t('LABEL_PERSONAL_DETAILS_EDITED_SUCCESS'));
      await refreshCustomer();
    }
  };

  return (
    <Formik
      initialValues={params}
      onSubmit={onSubmit}
      validationSchema={validationSchema(isUk, t)}
      enableReinitialize={true}
      validateOnChange={true}
    >
      {({ values, setValues }) => (
        <Form className={styles.formContent} noValidate>
          <FormikErrorsGtmLogger
            page_type="mon compte"
            form_type="mes informations personnelles"
          />
          <RadioFieldCustom
            label={t('LABEL_TITLE_GENDER', { ns: 'translation' })}
            name="civility"
            options={isUk ? genderOptionsUk : genderOptions}
            required
            extraStyle={extraStyleGender}
            autoComplete={AC.HonorificPrefix}
          />
          <TextFieldCustom
            label={t('LABEL_LASTNAME')}
            required
            extraStyle={extraStyleField}
            name="lastName"
            autoComplete={AC.LastName}
            disabled
          />
          <TextFieldCustom
            label={t('LABEL_FIRSTNAME')}
            required
            extraStyle={extraStyleField}
            name="firstName"
            autoComplete={AC.FirstName}
            disabled
          />
          <DateFieldCustom
            label={t('LABEL_DATEOFBIRTH')}
            name="dateOfBirth"
            disabled={isDisabledDate}
            extraStyle={extraStyleBirth}
            autoComplete={{
              day: AC.BirthDay,
              month: AC.BirthMonth,
              year: AC.BirthYear,
            }}
          />
          <TextFieldCustom
            label={
              isMobileAndPortraitTablet
                ? t('LABEL_CELL_PHONE_NUMBER_MOBILE')
                : t('LABEL_CELL_PHONE_NUMBER')
            }
            extraStyle={extraStyleField}
            name="cellPhone"
            type="tel"
            validation="number"
            onChange={e => {
              const value = e.target.value;
              const isEmptyField = value.length === 0;
              const isValidPhoneNumber = FIELDS_REGEX['number'].test(value);
              let newValues = {};

              if (isEmptyField && smsOptin) {
                newValues = { cellPhone: '', smsOption: '' };
              } else if (!isEmptyField && smsOptin && isValidPhoneNumber) {
                newValues = {
                  cellPhone: replacePhoneNumberWithSpace(value),
                  smsOption: smsOptin as string,
                };
              } else if (isValidPhoneNumber) {
                newValues = {
                  cellPhone: replacePhoneNumberWithSpace(value),
                };
              }

              setValues(prev => ({ ...prev, ...newValues }));
            }}
          />
          {values.cellPhone && smsOptin === null && (
            <RadioFieldCustom
              label={t('LABEL_CHECKOX_SMS')}
              name="smsOption"
              required
              options={smsOptions}
              extraStyle={extraStyleRadio}
              isErrorVisible={false}
            />
          )}
          <TextFieldCustom
            label={
              isMobileAndPortraitTablet
                ? t('LABEL_PHONE_NUMBER_MOBILE')
                : t('LABEL_PHONE_NUMBER')
            }
            extraStyle={extraStyleField}
            name="phone"
            type="tel"
            validation="number"
          />
          <div className={styles.button}>
            <Button>{t('LABEL_BUTTON')}</Button>
          </div>
          <ScrollToFieldError />
          <RequiredFields classes={{ wrapper: styles.requiredFieldsInfo }} />
        </Form>
      )}
    </Formik>
  );
}

export default PersonalDetailsInformations;
